import {
    Component,
    HostListener,
    Injector,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { UiRoutes } from '@lsq-frontend/angular-shared/app-constants';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { IdentityService } from '@lsq-frontend/fasttrack-shared/features/identity-service';
import { MfaBackupCodesService } from '@lsq-frontend/fasttrack-shared/features/mfa-backup-codes';
import { WhiteLabelManagerService } from '@lsq-frontend/fasttrack-shared/features/white-label-service';
import {
    ADMIN_BUYERS,
    ADMIN_OVERVIEW,
    ADMIN_PROSPECTS,
    ADMIN_INVOICES,
    ADMIN_PURCHASE_REQUESTS,
    VIEW_DOCUMENTS,
    ADMIN_SUPPLIERS,
    atlas,
    BROWSER_NOT_SUPPORTED,
    CONTACT_US,
    CUSTOMERS,
    INVOICES_UPLOAD,
    INVOICE_UPLOAD_HISTORY,
    LOCAL_CLAIMS,
    LOCAL_USER_STATE,
    LOGOUT,
    REPORTS,
    REPORTS_ACCOUNT_OVERVIEW,
    REPORTS_AGING,
    REPORTS_AVAILABLE_FUNDS_SUMMARY,
    REPORTS_COLLECTIONS,
    REPORTS_CREDIT_REQUEST_HISTORY,
    REPORTS_FUNDINGS,
    REPORTS_INELIGIBLES,
    REPORTS_PURCHASES,
    REQUEST_FUNDS,
    SELECT_ACCOUNT,
    SETTINGS,
    SETTINGS_MOBILE_MENU,
    supplyChainDirectAccountAtlas,
    SUPPLY_CHAIN_DIRECT,
    SUPPLY_CHAIN_DIRECT_ACCOUNT_INVOICES,
    SUPPLY_CHAIN_DIRECT_ACCOUNT_PAYMENTS,
    SUPPLY_CHAIN_DIRECT_ACCOUNT_SUPPLIERS,
    SUPPLY_CHAIN_DIRECT_BUYER_INSIGHTS,
    SUPPLY_CHAIN_DIRECT_BUYER_INVOICES,
    SUPPLY_CHAIN_DIRECT_BUYER_PAYMENTS,
    SUPPLY_CHAIN_DIRECT_BUYER_SUPPLIERS,
    SUPPLY_CHAIN_DIRECT_SELLER_BUYERS,
    SUPPLY_CHAIN_DIRECT_SELLER_CONTACT_US,
    SUPPLY_CHAIN_DIRECT_SELLER_INVOICES,
    SUPPLY_CHAIN_DIRECT_SELLER_PAYMENTS,
    SUPPLY_CHAIN_DIRECT_SELLER_REPORTS,
    SUPPLY_CHAIN_DIRECT_SELLER_REQUEST_EARLY_PAYMENT,
    SUPPLY_CHAIN_PLATFORM_SESSION_TIMEOUT,
    SUPPLY_CHAIN_DIRECT_BUYER_PAY_LATER,
    INSIGHTS,
    INSIGHTS_CUSTOMERS_BY_EXPOSURE
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import {
    GetCompanyNameRequest,
    GetCompanyNameService
} from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import {
    Application,
    DashboardClaim,
    ListActiveBannersRequest,
    User
} from '@lsq-frontend/remote-services/enterprise/common';
import { ListActiveBannersService } from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import {
    ActionSheet,
    ClassNames,
    Color,
    forLifeOf,
    getDynamicRouteLink,
    getNavigationItemForNode,
    LifecycleAware,
    NavigationItem,
    NavigationState,
    Orientation,
    Styling,
    ActionSheetService,
    Announcement,
    AnnouncementHeight,
    AnnouncementService,
    ClaimService,
    FeatureFlagService,
    getRouterLinkUrlById,
    Icons,
    LsqAppRootComponent,
    LsqNavigationComponent,
    ResponsiveService,
    SessionService,
    WindowReferenceService,
    UnsupportedBrowserModal
} from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { combineLatest, Observable } from 'rxjs';
import { filter, first, map, shareReplay, switchMap, delay } from 'rxjs/operators';
import { GlobalSearchService } from '../global-search/global-search.service';
import { NotificationsService } from '../notifications/notifications.service';
import { BeamerService } from '../services/beamer.service';
import { SavedDateFilterService } from '../services/saved-date-filter.service';
import { BuyerInsightsService } from '../supply-chain/direct/buyer-account/buyer/insights/insights.service';
import { ScfSellerService } from '../supply-chain/direct/seller/services/scf-seller.service';
import { SupplyChainPlatformRelationshipService } from '../supply-chain/platform/relationship/relationship.service';
import { SupplyChainDirectBuyerAccountSharedService } from '../supply-chain/direct/buyer-account/supply-chain-direct-buyer-account-shared.service';
import { DrApplicationService } from '@lsqlabs/dr-application-shared';
import { DocumentsService } from '../documents/documents.service';

// Zendesk Chat widget global variable.
declare const zE: any;

@LifecycleAware()
@Component({
    selector: 'fasttrack-monolith-entry',
    templateUrl: './entry.component.html',
    styleUrls: ['./entry.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class FasttrackMonolithEntryComponent
    extends LsqAppRootComponent
    implements OnInit, OnDestroy
{
    @ViewChild('mobileNavTemplate') public mobileNavTemplateRef: TemplateRef<any>;
    @ViewChild(LsqNavigationComponent) public lsqNavigationComponent: LsqNavigationComponent;
    public isClientTerminated = false;
    public isFactoringUser = false;
    public isFinancingUser = false;
    public isMobileUploadSession = false;
    public topOfPageAdjustment = 0;
    public navigationState = new NavigationState();
    public showFooter = true;
    public showCallUs = false;
    public isFullScreen = false;
    public hideNavigationItems = false;
    public isBuyerInsightsActivated = false;
    public isPayLaterEnabled = false;
    public isBuyerUser = false;
    public isAccountUser = false;
    public canAccessSwitchAccount = false;

    public canHaveNotifications$: Observable<boolean>;
    public userFullName$: Observable<string>;
    public isLoggedIn$: Observable<boolean>;
    public hasViewProfileClaim$: Observable<boolean>;
    public logoutNavigationItem = getNavigationItemForNode(atlas, LOGOUT, {
        queryParams: { immediate: true }
    });
    public settingsNavigationItem = getNavigationItemForNode(atlas, SETTINGS);
    public switchCompanyNavigationItem = getNavigationItemForNode(atlas, SELECT_ACCOUNT);
    public whiteLabel$ = this._whiteLabelManagerService.whiteLabel$;
    public hasMoreThanJustApplicationClaim$ = this.claimService.claimStream.pipe(
        map((claims) => Object.keys(claims).filter((claimKey) => claims[claimKey]).length > 1),
        shareReplay(1)
    );

    // Constants.
    public alertIcon = Icons.AlertError;
    public claim = DashboardClaim;
    public classNames = ClassNames;
    public color = Color;
    public featureFlags = FeatureFlags;
    public icons = Icons;
    public orientation = Orientation;
    public switchCompaniesIcon = Icons.SwitchCustomers;

    private _showCallUsWhiteList: RegExp[] = [/^\/enrollment/];
    private _fullScreenWhiteList: RegExp[] = [
        /^\/enrollment/,
        /^\/platform\/.*\/overview/,
        /^\/platform\/.*\/not-interested/,
        /^\/platform\/.*\/enroll/,
        /^\/platform\/.*\/next-steps/
    ];
    private _activeBannersRequest = new ListActiveBannersRequest({
        active: true,
        application: Application.Dashboard
    });

    // I18n navigation titles.
    public scfDirectPayLaterTitle = this._i18n({
        value: 'FlexTerm',
        meaning: 'Navigation bar title for supply chain flex term page'
    });
    public scfDirectInvoicesTitle = this._i18n({
        value: 'Invoices',
        meaning: 'Navigation bar title for supply chain invoices page'
    });
    public scfDirectHistoryTitle = this._i18n({
        value: 'History',
        meaning: 'Navigation bar title for supply chain history page'
    });
    public scfDirectPaymentsTitle = this._i18n({
        value: 'Payments',
        meaning: 'Navigation bar title for supply chain payments page'
    });
    public scfDirectContactUsTitle = this._i18n({
        value: 'Contact Us',
        meaning: 'Navigation bar title for supply chain contact us page'
    });
    public scfDirectBuyersTitle = this._i18n({
        value: 'Buyers',
        meaning: 'Navigation bar title for supply chain buyers page'
    });
    public scfDirectSuppliersTitle = this._i18n({
        value: 'Suppliers',
        meaning: 'Navigation bar title for supply chain suppliers page'
    });
    public scfDirectInsightsTitle = this._i18n({
        value: 'Insights',
        meaning: 'Navigation bar title for supply chain analytics page'
    });
    public scfRequestPaymentTitle = this._i18n({
        value: 'Request Payment',
        meaning: 'Navigation bar title for supply chain request payment page'
    });
    public scfReportsTitle = this._i18n({
        value: 'Reports',
        meaning: 'Navigation bar title for supply chain reports page'
    });
    public scfCreateNewReportTitle = this._i18n({
        value: 'Create New Report',
        meaning: 'Navigation bar title for supply chain create new report page'
    });
    public helpCenterTitle = this._i18n({
        value: 'Help Center',
        meaning: 'Menu title for Help Center'
    });

    public supplierHelpCenterNavigationItem = new NavigationItem({
        label: this.helpCenterTitle,
        externalLink: 'https://lsqcommunity.force.com/supplierhelp/s/',
        claims: [DashboardClaim.FinancingVendorPortal]
    });

    public buyerHelpCenterNavigationItem = new NavigationItem({
        label: this.helpCenterTitle,
        externalLink: 'https://lsqcommunity.force.com/buyerhelp/s/',
        claims: [DashboardClaim.FinancingBuyerPortal, DashboardClaim.FinancingAccountPortal]
    });

    constructor(
        private _featureFlagService: FeatureFlagService,
        private _i18n: I18n,
        private _userService: UserService,
        private _whiteLabelManagerService: WhiteLabelManagerService,
        private _savedDateFilterService: SavedDateFilterService,
        private _platformRelationshipService: SupplyChainPlatformRelationshipService,
        private _supplyChainDirectBuyerAccountSharedService: SupplyChainDirectBuyerAccountSharedService,
        private _sessionService: SessionService,
        private _getCompanyNameService: GetCompanyNameService,
        private _scfSellerService: ScfSellerService,
        private _buyerInsightsService: BuyerInsightsService,
        public responsiveService: ResponsiveService,
        public globalSearchService: GlobalSearchService,
        public notificationsService: NotificationsService,
        public actionSheetService: ActionSheetService,
        public announcementService: AnnouncementService,
        public claimService: ClaimService,
        public injector: Injector,
        public viewContainerRef: ViewContainerRef,
        public windowReferenceService: WindowReferenceService,
        public listActiveBannersService: ListActiveBannersService,
        public router: Router,
        public activatedRoute: ActivatedRoute,
        public beamerService: BeamerService,
        private _identityService: IdentityService,
        private _mfaBackupCodesService: MfaBackupCodesService,
        private _arService: DrApplicationService,
        private _documentsService: DocumentsService
    ) {
        super(injector, viewContainerRef, router);

        this._documentsService.fetchProspectData();

        // Need to make the observable hot.
        this.whiteLabel$.subscribe();
        this._initNavigation();

        combineLatest([this._userService.user$, this.activatedRoute.queryParams])
            .pipe(forLifeOf(this), delay(2000))
            .subscribe(([user, params]) => {
                if (params['showUpdateMfaModal'] === 'true') {
                    if (user?.currentCompany) {
                        this._mfaBackupCodesService.showUpdateMfaModal();
                    }
                }
            });
    }

    @HostListener('window:beforeunload')
    public async beforePageUnload(): Promise<void> {
        await this._sessionService.pageClosed();
    }

    public ngOnInit(): void {
        super.ngOnInit();

        // We allow the user to use partially supported browsers, but show a disclaimer modal.
        if (this.detectBrowserService.isPartiallySupportedBrowser()) {
            this.modalService.showModal(new UnsupportedBrowserModal());
        }

        // Check if current route needs to respect any of our rendering whitelists.
        // Do the same after route changes.
        this.checkRenderingWhitelists();
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                forLifeOf(this)
            )
            .subscribe(() => {
                this.checkRenderingWhitelists();
            });

        this.claimService
            .isOn(DashboardClaim.FactoringDashboard)
            .pipe(forLifeOf(this))
            .subscribe((isFactoringUser) => {
                this.isFactoringUser = isFactoringUser;
                this._initNavigation();
            });

        this.claimService
            .isOn(DashboardClaim.FinancingVendorPortal)
            .pipe(forLifeOf(this))
            .subscribe((isFinancingUser) => {
                this.isFinancingUser = isFinancingUser;
                this._initNavigation();
            });

        this.claimService
            .isOn(DashboardClaim.MobileInvoiceUpload)
            .pipe(first())
            .subscribe((claim) => {
                this.isMobileUploadSession = claim;
            });

        this.globalSearchService.isExpanded$.pipe(forLifeOf(this)).subscribe((isExpanded) => {
            this.topContentGlobalSearchClass = isExpanded ? ClassNames.TopContentSearchBar : '';
        });

        this.beamerService.initialize();

        combineLatest([
            this._featureFlagService.isOn(FeatureFlags.Chat),
            // Presently, we only want to show chat in vendor portal.
            this.claimService.isOn(DashboardClaim.FinancingVendorPortal),
            this._userService.user$
        ])
            .pipe(forLifeOf(this))
            .subscribe(([chatIsOn, isFinancingUser, user]) => {
                if (chatIsOn && isFinancingUser && user && user.currentCompany && zE) {
                    // Give zendesk widget the current user's information.
                    zE('webWidget', 'identify', {
                        name: `${user.firstName} ${user.lastName}`,
                        email: user.emailAddress,
                        organization: user.currentCompany.name
                    });
                    zE('webWidget', 'updateSettings', {
                        webWidget: {
                            chat: {
                                departments: {
                                    enabled: ['']
                                }
                            }
                        }
                    });

                    // It seems like zE can be destroyed by the time it gets here in some situations
                    if (zE) {
                        zE('webWidget', 'show');
                    }
                } else {
                    // End zendesk session.
                    zE('webWidget', 'logout');
                    zE('webWidget', 'hide');
                }
            });

        // This prevents the store from being stored over multiple sessions.
        this._userService.logout$.pipe(forLifeOf(this)).subscribe(() => {
            this._savedDateFilterService.clearAll();
            this.navigationState.user = null;
            this.navigationState.companies = [];
        });

        // Update announcements when the user changes.
        this._userService.user$.pipe(forLifeOf(this)).subscribe(async (user) => {
            if (user) {
                const response = await this.listActiveBannersService.executeOnce(
                    this._activeBannersRequest
                );
                response.rows
                    .filter((announcement) =>
                        user.claims.dashboard?.some((claim) =>
                            announcement.audienceClaims.includes(claim)
                        )
                    )
                    .forEach((announcement) => {
                        this.announcementService.addAnnouncement({
                            id: announcement.title,
                            text: announcement.text,
                            dismissable: true,
                            onClick: announcement.link
                                ? () => window.open(announcement.link, '_blank')
                                : undefined
                        });
                    });
            } else {
                this.announcementService.clearAnnouncements();
            }
        });

        this._scfSellerService.init();

        // Update navigationState and notifications when the user changes.
        this._userService.user$
            .pipe(
                filter((user) => !!user && !!user.currentCompany),
                forLifeOf(this)
            )
            .subscribe(async (user) => {
                this.navigationState.user = user;
                this.navigationState.companies = user.companies;
                await this.setCompanyName(user);
                this.notificationsService.refreshNotifications();
                this.idleService.reset();
            });

        // Check for unsupported browsers that cannot be caught by index.html
        if (this.detectBrowserService.isUnsupportedBrowser()) {
            this.router.navigateByUrl(getRouterLinkUrlById(atlas, BROWSER_NOT_SUPPORTED));
        }

        this.announcementService.announcementsStream
            .pipe(forLifeOf(this))
            .subscribe((value: Announcement[]) => {
                this.topOfPageAdjustment = Styling.NavBarHeight + value.length * AnnouncementHeight;
            });

        this.canHaveNotifications$ = this.claimService.isOn(DashboardClaim.FactoringDashboard);

        this.isLoggedIn$ = this._userService.user$.pipe(map((user) => !!user));
        this.userFullName$ = this._userService.user$.pipe(
            map((user) => {
                if (user) {
                    return `${user.firstName} ${user.lastName}`;
                }
            })
        );
        this.hasViewProfileClaim$ = this.claimService.isOn(DashboardClaim.ViewProfile);

        combineLatest([
            this.isLoggedIn$,
            this.claimService.isOn(DashboardClaim.FinancingBuyerPortal),
            this.featureFlagService.isOn(FeatureFlags.BuyerExperienceAnalyticsInsights)
        ])
            .pipe(
                filter(
                    ([isLoggedIn, isBuyer, isAnalyticsFeatureOn]) =>
                        isLoggedIn && isBuyer && isAnalyticsFeatureOn
                ),
                switchMap(() => this._buyerInsightsService.isActivatedForBuyer())
            )
            .subscribe((isInsightsActivated) => {
                this.isBuyerInsightsActivated = isInsightsActivated;
                this._initNavigation();
            });

        combineLatest([
            this.isLoggedIn$,
            this.claimService.isOn(DashboardClaim.FinancingBuyerPortal),
            this.claimService.isOn(DashboardClaim.FinancingAccountPortal)
        ])
            .pipe(filter(([isLoggedIn, isBuyer, isAccount]) => isLoggedIn))
            .subscribe(([isLoggedIn, isBuyer, isAccount]) => {
                this.isBuyerUser = isBuyer;
                this.isAccountUser = isAccount;
                this._initNavigation();
            });

        this._supplyChainDirectBuyerAccountSharedService.isPayLaterEnabled$.subscribe(
            (isPayLaterEnabled) => {
                this.isPayLaterEnabled = isPayLaterEnabled;
                this._initNavigation();
            }
        );

        this._identityService.hasMultipleCompaniesOrANetworkRequest$.subscribe(
            (hasMultipleCompaniesOrANetworkRequest) => {
                this.canAccessSwitchAccount = hasMultipleCompaniesOrANetworkRequest;
                this._initNavigation();
            }
        );

        // Configure the IdleService.
        this.idleService.setRouteUrlBlacklist([
            `${UiRoutes.Login}($|\\?)`,
            `${getRouterLinkUrlById(atlas, SUPPLY_CHAIN_DIRECT, { relative: true })}/($|\\?)`,
            'timeout($|\\?)'
        ]);
        this.idleService.start();
        this.idleService.countdownStartedStream.subscribe(() => this.displaySessionIdleModal());
        this.idleService.countdownFinishedStream.subscribe(async () => {
            await this.handleIdleServiceCountdownFinished();
        });
    }

    public ngOnDestroy(): void {
        this.idleService.stop();
    }

    public checkRenderingWhitelists(): void {
        this.isFullScreen = this._fullScreenWhiteList.some(
            (whiteListedPattern) => !!this.router.url.match(new RegExp(whiteListedPattern, 'gi'))
        );

        this.showCallUs = this._showCallUsWhiteList.some(
            (whiteListedPattern) => !!this.router.url.match(new RegExp(whiteListedPattern, 'gi'))
        );
    }

    public async handleIdleServiceCountdownFinished(): Promise<void> {
        const platformId = await this._platformRelationshipService.platformId$
            .pipe(first())
            .toPromise();

        if (platformId) {
            const platformSessionTimeoutLink = getDynamicRouteLink(
                getRouterLinkUrlById(atlas, SUPPLY_CHAIN_PLATFORM_SESSION_TIMEOUT),
                { platformId }
            );
            await this._sessionService.timeout(platformSessionTimeoutLink);
        } else {
            // Force logging out the user because they have timed out.
            await this._sessionService.timeout();
        }
        this.modalService.closeModal();
        this.idleModalIsOpen = false;
        this.idleService.reset();
    }

    public openMobileNav(): void {
        this.actionSheetService.showActionSheet(
            new ActionSheet({
                templateRef: this.mobileNavTemplateRef,
                closeButtonColor: Color.White,
                stickToTop: true
            })
        );
    }

    private _initNavigation(): void {
        this._buildPrimaryNavigationItems();
        this._buildSettingsDropdownNavigationItems();
        this._buildAllNavigationItems();
    }

    private _buildAllNavigationItems(): void {
        this.navigationState.allNavigationItems = [
            ...this.navigationState.navigationItems,
            ...this.navigationState.settingsDropdownNavigationItems
        ];
    }

    private _buildPrimaryNavigationItems(): void {
        this.navigationState.navigationItems = [
            getNavigationItemForNode(atlas, ADMIN_SUPPLIERS),
            getNavigationItemForNode(atlas, ADMIN_BUYERS),
            getNavigationItemForNode(atlas, ADMIN_PROSPECTS),
            getNavigationItemForNode(atlas, ADMIN_INVOICES),
            getNavigationItemForNode(atlas, ADMIN_PURCHASE_REQUESTS),

            getNavigationItemForNode(atlas, SUPPLY_CHAIN_DIRECT_SELLER_REQUEST_EARLY_PAYMENT, {
                label: this.scfRequestPaymentTitle
            }),
            getNavigationItemForNode(atlas, SUPPLY_CHAIN_DIRECT_SELLER_INVOICES, {
                label: this.scfDirectInvoicesTitle
            }),
            getNavigationItemForNode(atlas, SUPPLY_CHAIN_DIRECT_SELLER_PAYMENTS, {
                label: this.scfDirectPaymentsTitle
            }),
            getNavigationItemForNode(atlas, SUPPLY_CHAIN_DIRECT_SELLER_BUYERS, {
                label: this.scfDirectBuyersTitle
            }),
            getNavigationItemForNode(atlas, SUPPLY_CHAIN_DIRECT_BUYER_INVOICES, {
                label: this.scfDirectInvoicesTitle
            }),
            getNavigationItemForNode(atlas, SUPPLY_CHAIN_DIRECT_ACCOUNT_INVOICES, {
                label: this.scfDirectInvoicesTitle
            }),
            getNavigationItemForNode(atlas, SUPPLY_CHAIN_DIRECT_BUYER_SUPPLIERS, {
                label: this.scfDirectSuppliersTitle
            }),
            getNavigationItemForNode(atlas, SUPPLY_CHAIN_DIRECT_ACCOUNT_SUPPLIERS, {
                label: this.scfDirectSuppliersTitle
            }),

            ...(this.isPayLaterEnabled
                ? [
                      getNavigationItemForNode(atlas, SUPPLY_CHAIN_DIRECT_BUYER_PAY_LATER, {
                          label: this.scfDirectPayLaterTitle
                      })
                  ]
                : []),

            ...(this.isBuyerInsightsActivated
                ? [
                      getNavigationItemForNode(atlas, SUPPLY_CHAIN_DIRECT_BUYER_INSIGHTS, {
                          label: this.scfDirectInsightsTitle,
                          routerLinkActiveOptions: { exact: false }
                      })
                  ]
                : []),
            getNavigationItemForNode(atlas, SUPPLY_CHAIN_DIRECT_BUYER_PAYMENTS),
            getNavigationItemForNode(atlas, SUPPLY_CHAIN_DIRECT_ACCOUNT_PAYMENTS, {
                label: this.scfDirectPaymentsTitle,
                featureFlags:
                    supplyChainDirectAccountAtlas.SUPPLY_CHAIN_DIRECT_ACCOUNT_PAYMENTS.FeatureFlags
            }),
            getNavigationItemForNode(atlas, REQUEST_FUNDS),
            getNavigationItemForNode(atlas, REPORTS, {
                routerLinkActiveOptions: { exact: false },
                children: [
                    getNavigationItemForNode(atlas, REPORTS_PURCHASES),
                    getNavigationItemForNode(atlas, REPORTS_ACCOUNT_OVERVIEW),
                    getNavigationItemForNode(atlas, REPORTS_AGING),
                    getNavigationItemForNode(atlas, REPORTS_COLLECTIONS),
                    getNavigationItemForNode(atlas, REPORTS_FUNDINGS),
                    getNavigationItemForNode(atlas, REPORTS_AVAILABLE_FUNDS_SUMMARY),
                    getNavigationItemForNode(atlas, REPORTS_INELIGIBLES),
                    getNavigationItemForNode(atlas, REPORTS_CREDIT_REQUEST_HISTORY)
                ]
            }),
            getNavigationItemForNode(atlas, CUSTOMERS),
            getNavigationItemForNode(atlas, INVOICE_UPLOAD_HISTORY),
            getNavigationItemForNode(atlas, VIEW_DOCUMENTS, {
                counter: this._arService.getOpenRequestTotalDocuments()
            }),
            getNavigationItemForNode(atlas, INSIGHTS, {
                routerLinkActiveOptions: { exact: false },
                children: [getNavigationItemForNode(atlas, INSIGHTS_CUSTOMERS_BY_EXPOSURE)]
            }),
            getNavigationItemForNode(atlas, INVOICES_UPLOAD, {
                icon: Icons.Add,
                linkClassName: 'text-green text-uppercase'
            }),
            getNavigationItemForNode(atlas, SUPPLY_CHAIN_DIRECT_SELLER_REPORTS, {
                label: this.scfReportsTitle
            })
        ];
    }

    private _buildSettingsDropdownNavigationItems(): void {
        const settingsNavigationItems: NavigationItem[] = [];
        settingsNavigationItems.push(getNavigationItemForNode(atlas, LOCAL_CLAIMS));
        settingsNavigationItems.push(getNavigationItemForNode(atlas, LOCAL_USER_STATE));
        settingsNavigationItems.push(getNavigationItemForNode(atlas, ADMIN_OVERVIEW));
        settingsNavigationItems.push(this.supplierHelpCenterNavigationItem);
        settingsNavigationItems.push(this.buyerHelpCenterNavigationItem);
        settingsNavigationItems.push(getNavigationItemForNode(atlas, CONTACT_US));
        settingsNavigationItems.push(
            getNavigationItemForNode(atlas, SUPPLY_CHAIN_DIRECT_SELLER_CONTACT_US, {
                label: this.scfDirectContactUsTitle
            })
        );
        settingsNavigationItems.push(
            getNavigationItemForNode(atlas, SETTINGS_MOBILE_MENU, {
                hideInDesktop: true
            })
        );
        if (this.canAccessSwitchAccount) {
            settingsNavigationItems.push({
                ...this.switchCompanyNavigationItem,
                hideInDesktop: true
            });
        }
        settingsNavigationItems.push({
            ...this.logoutNavigationItem,
            hideInDesktop: true
        });

        this.navigationState.settingsDropdownNavigationItems = settingsNavigationItems;
    }

    private async setCompanyName(user: User) {
        if (
            this.claimService.isOnSync(DashboardClaim.FinancingVendorPortal) ||
            this.claimService.isOnSync(DashboardClaim.FinancingBuyerPortal)
        ) {
            const { name } = await this._getCompanyNameService.executeOnce(
                new GetCompanyNameRequest({
                    companyKey: this.navigationState.user.currentCompany.companyKey
                })
            );
            this.navigationState.companyName = name;
            this._identityService.companyName$.next(name);
        } else {
            this.navigationState.companyName = user.currentCompany.name;
            this._identityService.companyName$.next(user.currentCompany.name);
        }
    }
}
